import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { TextArea } from '../../../components/TextArea';

import { Trash2, Edit3, Plus, X, Settings } from 'lucide-react';
import { api } from '../../../services/api';
import { enqueueSnackbar } from 'notistack';
import { Grupo, GrupoConfiguracao, CreateGrupoConfiguracaoDTO, UpdateGrupoConfiguracaoDTO } from '../../../types/grupos';

interface GrupoConfiguracaoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  grupo: Grupo;
}

interface FormData {
  chave: string;
  valor: string;
  tipo_valor: 'string' | 'number' | 'boolean' | 'json';
  descricao: string;
  categoria: string;
  publico: boolean;
  editavel: boolean;
}

const initialFormData: FormData = {
  chave: '',
  valor: '',
  tipo_valor: 'string',
  descricao: '',
  categoria: 'geral',
  publico: false,
  editavel: true
};

const GrupoConfiguracaoModal: React.FC<GrupoConfiguracaoModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  grupo
}) => {
  const [configuracoes, setConfiguracoes] = useState<GrupoConfiguracao[]>([]);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && grupo) {
      loadConfiguracoes();
    } else {
      setConfiguracoes([]);
      setFormData(initialFormData);
      setEditingId(null);
      setShowForm(false);
    }
  }, [isOpen, grupo]);

  const loadConfiguracoes = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/grupos-configuracoes/grupo/${grupo.id}`);
      setConfiguracoes(response.data);
    } catch (error: any) {
      enqueueSnackbar('Erro ao carregar configurações do grupo', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config: GrupoConfiguracao) => {
    setFormData({
      chave: config.chave,
      valor: config.valor,
      tipo_valor: config.tipo_valor,
      descricao: config.descricao || '',
      categoria: config.categoria,
      publico: config.publico,
      editavel: config.editavel
    });
    setEditingId(config.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja deletar esta configuração?')) return;

    try {
      await api.delete(`/grupos-configuracoes/${id}`);
      enqueueSnackbar('Configuração deletada com sucesso!', { variant: 'success' });
      await loadConfiguracoes();
      onUpdate();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao deletar configuração', { variant: 'error' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      if (editingId) {
        const updateData: UpdateGrupoConfiguracaoDTO = {
          ...formData
        };
        await api.put(`/grupos-configuracoes/${editingId}`, updateData);
        enqueueSnackbar('Configuração atualizada com sucesso!', { variant: 'success' });
      } else {
        const createData: CreateGrupoConfiguracaoDTO = {
          ...formData,
          grupo_id: grupo.id
        };
        await api.post('/grupos-configuracoes', createData);
        enqueueSnackbar('Configuração criada com sucesso!', { variant: 'success' });
      }

      setFormData(initialFormData);
      setEditingId(null);
      setShowForm(false);
      await loadConfiguracoes();
      onUpdate();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao salvar configuração', { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatValue = (valor: string, tipo: string) => {
    switch (tipo) {
      case 'boolean':
        return valor === 'true' ? 'Sim' : 'Não';
      case 'json':
        try {
          return JSON.stringify(JSON.parse(valor), null, 2);
        } catch {
          return valor;
        }
      default:
        return valor;
    }
  };

  const categorias = [
    { value: 'geral', label: 'Geral' },
    { value: 'seguranca', label: 'Segurança' },
    { value: 'notificacoes', label: 'Notificações' },
    { value: 'permissoes', label: 'Permissões' },
    { value: 'integracao', label: 'Integração' },
    { value: 'personalizacao', label: 'Personalização' },
    { value: 'avancado', label: 'Avançado' }
  ];

  const tiposValor = [
    { value: 'string', label: 'Texto' },
    { value: 'number', label: 'Número' },
    { value: 'boolean', label: 'Verdadeiro/Falso' },
    { value: 'json', label: 'JSON' }
  ];

  const configuracoesPorCategoria = configuracoes.reduce((acc, config) => {
    if (!acc[config.categoria]) {
      acc[config.categoria] = [];
    }
    acc[config.categoria].push(config);
    return acc;
  }, {} as Record<string, GrupoConfiguracao[]>);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5" />
            <span>Configurações do Grupo: {grupo?.grupo}</span>
          </DialogTitle>
          <DialogDescription>
            Gerencie as configurações específicas deste grupo. Configure parâmetros, permissões e integrações.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Botão para adicionar nova configuração */}
            {!showForm && (
              <Button
                onClick={() => setShowForm(true)}
                className="w-full"
                variant="outline"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Nova Configuração
              </Button>
            )}

            {/* Formulário */}
            {showForm && (
              <div className="border rounded-lg p-4 bg-muted/50">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-foreground">
                    {editingId ? 'Editar Configuração' : 'Nova Configuração'}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowForm(false);
                      setFormData(initialFormData);
                      setEditingId(null);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="chave">Chave da Configuração *</Label>
                      <Input
                        id="chave"
                        value={formData.chave}
                        onChange={(e) => setFormData(prev => ({ ...prev, chave: e.target.value }))}
                        placeholder="Ex: limite_membros, permite_subgrupos..."
                        required
                        disabled={!!editingId}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="categoria">Categoria</Label>
                      <select
                        id="categoria"
                        value={formData.categoria}
                        onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
                        className="flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="">Selecione a categoria</option>
                        {categorias.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <TextArea
                      name="descricao"
                      title="Descrição"
                      value={formData.descricao}
                      onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                      placeholder="Descreva o que esta configuração controla..."
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="tipo_valor">Tipo do Valor *</Label>
                      <select
                        id="tipo_valor"
                        value={formData.tipo_valor}
                        onChange={(e) =>
                          setFormData(prev => ({ ...prev, tipo_valor: e.target.value as 'string' | 'number' | 'boolean' | 'json' }))}
                        className="flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        required
                      >
                        <option value="">Selecione o tipo</option>
                        {tiposValor.map(tipo => (
                          <option key={tipo.value} value={tipo.value}>
                            {tipo.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="valor">Valor *</Label>
                      {formData.tipo_valor === 'boolean' ? (
                        <select
                          id="valor"
                          value={formData.valor}
                          onChange={(e) => setFormData(prev => ({ ...prev, valor: e.target.value }))}
                          className="flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          required
                        >
                          <option value="">Selecione o valor</option>
                          <option value="true">Verdadeiro</option>
                          <option value="false">Falso</option>
                        </select>
                      ) : formData.tipo_valor === 'json' ? (
                        <TextArea
                          name="valor"
                          value={formData.valor}
                          onChange={(e) => setFormData(prev => ({ ...prev, valor: e.target.value }))}
                          placeholder='{"exemplo": "valor"}'
                          rows={3}
                          required
                        />
                      ) : (
                        <Input
                          id="valor"
                          type={formData.tipo_valor === 'number' ? 'number' : 'text'}
                          value={formData.valor}
                          onChange={(e) => setFormData(prev => ({ ...prev, valor: e.target.value }))}
                          placeholder="Digite o valor..."
                          required
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-6">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="publico"
                        checked={formData.publico}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, publico: checked }))}
                      />
                      <Label htmlFor="publico">Configuração Pública</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="editavel"
                        checked={formData.editavel}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, editavel: checked }))}
                      />
                      <Label htmlFor="editavel">Editável</Label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowForm(false);
                        setFormData(initialFormData);
                        setEditingId(null);
                      }}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Lista de configurações por categoria */}
            <div className="space-y-6">
              {Object.keys(configuracoesPorCategoria).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma configuração definida para este grupo ainda.
                </div>
              ) : (
                Object.entries(configuracoesPorCategoria).map(([categoria, configs]) => (
                  <div key={categoria} className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-medium capitalize">
                        {categorias.find(c => c.value === categoria)?.label || categoria}
                      </h3>
                      <Badge variant="outline" className="text-xs">
                        {configs.length} configuração(ões)
                      </Badge>
                    </div>

                    <div className="grid gap-4">
                      {configs.map(config => (
                        <div key={config.id} className="border border-border rounded-lg p-4 bg-background">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="font-medium font-mono text-sm bg-muted px-2 py-1 rounded text-foreground">
                                  {config.chave}
                                </h4>
                                <Badge variant="outline" className="text-xs">
                                  {tiposValor.find(t => t.value === config.tipo_valor)?.label}
                                </Badge>
                                {config.publico && (
                                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                    Público
                                  </Badge>
                                )}
                                {!config.editavel && (
                                  <Badge variant="outline" className="text-xs bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                                    Somente Leitura
                                  </Badge>
                                )}
                              </div>

                              {config.descricao && (
                                <p className="text-sm text-muted-foreground mb-2">{config.descricao}</p>
                              )}

                              <div className="bg-muted/50 rounded p-2 text-sm font-mono">
                                <span className="text-muted-foreground">Valor: </span>
                                <span className="text-foreground">
                                  {formatValue(config.valor, config.tipo_valor)}
                                </span>
                              </div>

                              <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                                <span>Criado: {new Date(config.created_at).toLocaleDateString('pt-BR')}</span>
                                {config.updated_at !== config.created_at && (
                                  <span>Atualizado: {new Date(config.updated_at).toLocaleDateString('pt-BR')}</span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center space-x-2 ml-4">
                              {config.editavel && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(config)}
                                >
                                  <Edit3 className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(config.id)}
                                  className="text-red-600 hover:text-red-700 hover:border-red-300 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/30"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GrupoConfiguracaoModal;
